import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Typography, CircularProgress, Alert, Chip, Stack, Button, Avatar,
} from '@mui/material';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from 'recharts';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InventoryIcon from '@mui/icons-material/Inventory2';
import BoltIcon from '@mui/icons-material/Bolt';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import StorageIcon from '@mui/icons-material/Storage';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import {
  StatCard, ChartCard, areaGradient, trendFromSeries, fmtCompact, fmtBytes, ManualRefreshNotice,
} from '../../components/analytics/AnalyticsKit';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      tenantApi.getMyUsage('30d'),
      tenantApi.listInventory({ limit: 50, lowStock: true }),
    ])
      .then(([usage, inv]) => {
        setSummary(usage);
        // pick items with qty ≤ 10
        const ls = (inv.items || []).filter((i) => i.quantity <= 10).slice(0, 8);
        setLowStock(ls);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) { return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>; }
  if (error) { return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>; }

  const daily = summary?.dailyUsage || [];
  const reqTrend = trendFromSeries(daily, 'apiRequests');
  const reqSpark = daily.map((d) => ({ v: d.apiRequests }));
  const itemCount = summary?.current?.itemCount ?? 0;
  const maxItems = summary?.quotas?.maxItems ?? 5000;

  return (
    <Box sx={{ p: 3, maxWidth: 1240 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2.5} flexWrap="wrap" rowGap={1}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">Inventory health and API activity, last 30 days</Typography>
        </Box>
        <ManualRefreshNotice />
      </Stack>

      {/* Stats row */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Inventory Items"
            value={fmtCompact(itemCount)}
            sub={`of ${fmtCompact(maxItems)} allowed (${maxItems > 0 ? Math.round((itemCount / maxItems) * 100) : 0}%)`}
            icon={<InventoryIcon />} color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Low Stock Items"
            value={lowStock.length}
            sub="qty ≤ 10"
            color={lowStock.length > 0 ? '#ef6c00' : '#2e7d32'}
            icon={<WarningAmberIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="API Requests (30d)"
            value={fmtCompact(summary?.apiUsage?.totalRequests ?? 0)}
            trend={reqTrend} spark={reqSpark}
            icon={<BoltIcon />} color="#00897b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {summary?.storageBytes !== null && summary?.storageBytes !== undefined ? (
            <StatCard label="Storage Used" value={fmtBytes(summary.storageBytes)}
              sub="files + database (daily snapshot)" icon={<StorageIcon />} color="#7b1fa2" />
          ) : (
            <StatCard label="Active API Keys" value={summary?.current?.apiKeyCount ?? '—'}
              sub={`of ${summary?.quotas?.maxApiKeys ?? 3} allowed`} icon={<VpnKeyIcon />} color="#7b1fa2" />
          )}
        </Grid>
      </Grid>

      {/* Charts + low stock */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <ChartCard title="API Activity — Last 30 Days" subtitle="every request is metered, reads included">
            {daily.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={daily}>
                  <defs>
                    {areaGradient('dReq', '#1976d2')}
                    {areaGradient('dErr', '#d32f2f')}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,.08)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)}
                    tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
                  <ChartTooltip />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="apiRequests" name="Requests" stroke="#1976d2"
                    strokeWidth={2} fill="url(#dReq)" dot={false} />
                  <Area type="monotone" dataKey="errors" name="Errors" stroke="#d32f2f"
                    strokeWidth={2} fill="url(#dErr)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary" variant="body2">No usage data yet — activity appears here as your team uses the platform.</Typography>
              </Box>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartCard title="Low Stock Items"
            action={
              <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/inventory')}>
                View All
              </Button>
            }>
            {lowStock.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">All items well-stocked.</Typography>
              </Box>
            ) : (
              <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
                {lowStock.map((item) => (
                  <Stack key={item.itemId} direction="row" spacing={1.25} alignItems="center"
                    onClick={() => navigate(`/inventory/${item.itemId}`)}
                    sx={{ px: 1, py: 1, cursor: 'pointer', borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}>
                    <Avatar variant="rounded" sx={{
                      width: 30, height: 30, fontSize: 13, fontWeight: 700,
                      bgcolor: item.quantity <= 0 ? '#c6282822' : '#ef6c0022',
                      color: item.quantity <= 0 ? '#c62828' : '#ef6c00',
                    }}>
                      {(item.name || '?').slice(0, 1).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.sku}</Typography>
                    </Box>
                    <Chip label={item.quantity <= 0 ? 'Out' : item.quantity} size="small"
                      color={item.quantity <= 0 ? 'error' : 'warning'} sx={{ fontWeight: 700 }} />
                  </Stack>
                ))}
              </Stack>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}
